"use client";

import Link from "next/link";
import {
  Landmark, Bitcoin, DollarSign, Cpu, Palette, Leaf,
  Globe2, Earth, LineChart, MessageCircle, Rocket,
  Vote, Trophy, Dumbbell, Film, Plane, Scale, FlaskConical,
  Gamepad2, Music, Heart, Info,
} from "lucide-react";
import { TbBallFootball } from "react-icons/tb";
import { useT } from "@/hooks/useT";

const CATEGORY_ICONS: Record<string, any> = {
  politics: Landmark,
  sports: TbBallFootball,
  crypto: Bitcoin,
  finance: DollarSign,
  geopolitics: Globe2,
  tech: Cpu,
  culture: Palette,
  world: Earth,
  economy: LineChart,
  "climate-science": Leaf,
  climate: Leaf,
  mentions: MessageCircle,
  elections: Vote,
  elon: Rocket,
  entertainment: Film,
  gaming: Gamepad2,
  music: Music,
  health: Heart,
  science: FlaskConical,
  travel: Plane,
  legal: Scale,
  awards: Trophy,
  fitness: Dumbbell,
};

function getCategoryIcon(category: string) {
  const key = category.toLowerCase().replace(/\s+/g, "-");
  return CATEGORY_ICONS[key] || Globe2;
}

function getCategorySlug(category: string): string {
  return category.toLowerCase().replace(/\s+/g, "-");
}

export default function TraderAboutCard({
  bio,
  categories,
  displayName,
}: {
  bio: string | null;
  categories: string[];
  displayName: string;
}) {
  const t = useT();
  if (!bio && categories.length === 0) return null;

  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
      <div className="p-5">
        {/* About — auto-generated from the trader's stats by
            generateAboutText() in /traders/[identifier]/page.tsx.
            Marked explicitly with a "(Auto-generated)" hint so the
            user reads it as derived metadata, not the trader's own
            bio. Pika 2026-05-22: previously the block had no clue
            it was machine-built and read as a "robotic" copy. */}
        {bio && (
          <div className={categories.length > 0 ? "mb-5" : ""}>
            <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              <Info className="w-3.5 h-3.5" />
              {t("traders.about.title")}
              <span className="ml-auto text-[10px] font-normal normal-case tracking-normal text-gray-500 dark:text-gray-500 italic" title="Auto-generated from trading stats">
                {t("traders.about.autoGenerated")}
              </span>
            </h3>
            {/* Quiet neutral accent on the left of the bio — signals this
                block is the trader's identity summary (lime retired 2026-06-10). */}
            <div className="border-l-2 border-gray-200 dark:border-white/15 pl-3 py-0.5">
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                {bio}
              </p>
            </div>
          </div>
        )}

        {/* Categories */}
        {categories.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              {t("traders.about.markets")}
            </h3>
            <div className="flex flex-wrap gap-2">
              {categories.slice(0, 8).map((cat) => {
                const Icon = getCategoryIcon(cat);
                const slug = getCategorySlug(cat);
                return (
                  <Link
                    key={cat}
                    href={`/polymarket/markets/${slug}`}
                    className="group inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#0f0f12] border border-transparent rounded-[5px] hover:bg-gray-200 dark:hover:bg-white/[0.06] hover:border-gray-300 dark:hover:border-white/15 hover:text-gray-900 dark:hover:text-white transition-colors capitalize"
                  >
                    <Icon className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 shrink-0 transition-colors" />
                    {cat}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
